import {Rectangle} from "pixi.js";
import {ElevationApp} from "../../ElevationApp";
import {AddFx, ChildView} from "../../shared-2d/ChildView";
import {DrawFrmThing} from "../../drawfrm/DrawFrmThing";
import {Label} from "../../shared-2d/Label";
import {MdHardwarePlacement} from "../../model/hardware/MdHardware";
import {HardwareItem} from "../../../../../../api/Hardware";
import {placedOnLeaf} from "../hardware/hardwarePreps";
import {drawCutMarker} from "../../shared-2d/cutHatch";
import {inches, lineBlueHeavy, panelMetalGrey} from "../../const";
import {EdgeHardwareThing, EdgeRect} from "./EdgeHardwareThing";
import {doorFaceOf} from "../frameProfile/Helpers";
import {doorEdgeOf} from "../../model/hardware/MdHardwareRules";
import {Subscription} from "nate-react-api-helpers";

// the caption over the edge, matching the other columns (see LocationsView).
const CAPTION_FONT = 60;

// the hinge leaf mortised into the door edge: from the barrel face across to the backset
// off the other face (scene units = mm).
const DOOR_HINGE_BACKSET = 1 / 4 * inches;
const CUTOUT_RADIUS = 0.125 * inches;
const OUTLINE = 0.2 * inches;

// the label beside the edge naming a hinge that is not the group's default hinge (a
// power transfer hinge) or an item in the edge (a power transfer), off the edge's far
// side at its height.
const EDGE_LABEL_FONT = 40;
const EDGE_LABEL_GAP = 1 * inches;

// The hinge edge of a door leaf, drawn beside the hinge jamb as the fold-out's far side
// (back face | hinge edge | hinge jamb), so the hinge mortises line up with the jamb's.
// Like the latch edge (see LatchEdgeView) without the head: the edge is the door
// thickness wide and the leaf tall, on the same vertical origin (top of the frame jamb at
// y=0, the overcut below it). The back face is at x=0, the front face at the door
// thickness. It shows the hinge leaf mortises at the hinge heights, naming a hinge that
// is not the default (a power transfer hinge), and the door placements that live in the
// edge (edge-only items, see HardwarePlacementRule.edgeOnly) as EdgeHardwareThings.
export class DoorHingeView extends ChildView {
    active: boolean;

    private caption: Label;
    private captionGap: number;

    parentThing: DrawFrmThing;
    private edge: DrawFrmThing;

    private hardwareThings: {thing: EdgeHardwareThing; placement: MdHardwarePlacement; item: HardwareItem}[] = [];
    private placementSubs: Subscription[] = [];
    // the labels beside the edge (hinges and items), rebuilt each redraw.
    private edgeLabels: Label[] = [];
    private hingeSubs: Subscription[] = [];

    constructor(elev: ElevationApp, add: AddFx, active: boolean, captionGap: number) {
        super(elev, add);
        this.active = active;
        this.captionGap = captionGap;

        this.dimensionMode.emit(false);

        this.caption = new Label({text: "Door Hinge", fontSize: CAPTION_FONT, color: lineBlueHeavy, anchorX: 0.5, anchorY: 1});
        this.add(this.caption);

        this.parentThing = new DrawFrmThing();
        this.add(this.parentThing);

        this.edge = new DrawFrmThing();
        this.parentThing.add(this.edge);

        this.edge.makeInteractive({
            onClick: () => {
                if (this.elev.draw.isPanSuspended()) return;
                this.elev.viewSettings.clearSelection();
            },
        });

        const relayout = () => {
            this.redraw();
            this.elev.draw.currentScene?.applyLayout();
        };

        this.buildHardwareItems();
        this.trackSubscription(elev.hardware.listChanged.subscribe(() => {
            this.buildHardwareItems();
            this.redraw();
        }));

        this.watchHinges();
        this.trackSubscription(elev.hardware.hinges.listChanged.subscribe(() => {
            this.watchHinges();
            this.redraw();
        }));

        this.trackSubscription(elev.doorSpec.actualHeight.subscribe(relayout));
        this.trackSubscription(elev.doorSpec.doorThickness.subscribe(relayout));
        this.trackSubscription(elev.doorSpec.overcut.subscribe(relayout));
        this.trackSubscription(elev.doorSpec.undercut.subscribe(relayout));

        this.redraw();
    }

    private edgeRect(): EdgeRect {
        return {x0: 0, x1: this.elev.doorSpec.doorThickness.get()};
    }

    // (re)build one edge drawing per door placement that lives in this leaf's hinge edge.
    private buildHardwareItems() {
        for (const t of this.hardwareThings) {
            if (!t.thing.destroyed) t.thing.destroy();
        }
        this.hardwareThings = [];
        for (const sub of this.placementSubs) sub.cancel();
        this.placementSubs = [];

        const hw = this.elev.hardwareGroup?.hardware ?? [];
        for (const placement of this.elev.hardware.list.filter(p => placedOnLeaf(p, this.active))) {
            const item = hw.find(h => h.product === placement.productId);
            if (!item || doorEdgeOf(item) !== "hinge") continue;

            const thing = new EdgeHardwareThing({
                elev: this.elev,
                item: placement,
                product: item,
                edge: () => this.edgeRect(),
                // the back face is at x0 here (the fold-out's far side).
                frontFace: () => doorFaceOf(this.elev, "back", this.active),
                onSelect: (additive) => this.selectHardwareItem(placement, additive),
            });
            this.edge.lines.addChild(thing.graphic);
            this.hardwareThings.push({thing, placement, item});
            // the label follows the item's height.
            this.placementSubs.push(placement.y.subscribe(() => { if (!this.edge.destroyed) this.redraw(); }));
        }
    }

    private selectHardwareItem(item: MdHardwarePlacement, additive: boolean) {
        this.elev.viewSettings.selectItem({type: "hardware-item", id: item.id, kind: item.target, productId: item.productId}, additive);
        this.elev.viewSettings.activeTab.emit("selected");
    }

    // follow the hinge heights (a hinge dimension is edited on the faces).
    private watchHinges() {
        for (const sub of this.hingeSubs) sub.cancel();
        this.hingeSubs = this.elev.hardware.hinges.list.map(h => h.offsetTop.subscribe(() => { if (!this.edge.destroyed) this.redraw(); }));
    }

    redraw() {
        const door = this.elev.doorSpec;
        const thickness = door.doorThickness.get();
        const height = door.actualHeight.get();
        const overcut = door.overcut.get();
        const undercut = door.undercut.get();

        const g = this.edge.lines;
        g.clear();

        g.lineStyle(2, lineBlueHeavy, 1);
        g.beginFill(panelMetalGrey, 1);
        g.drawRect(0, overcut, thickness, height);
        g.endFill();

        if (overcut > 0) drawCutMarker(g, 0, 0, thickness, overcut);
        if (undercut > 0) drawCutMarker(g, 0, overcut + height, thickness, undercut);

        // the hinge leaves: the barrel sits on the face the leaf swings toward (the pull
        // face), the leaf runs from there to the backset off the other face.
        const back = doorFaceOf(this.elev, "back", this.active);
        const barrelX = back === "pull" || back === null ? 0 : thickness;
        const leafWidth = Math.max(0, thickness - DOOR_HINGE_BACKSET);
        const leafX = barrelX === 0 ? 0 : thickness - leafWidth;

        for (const l of this.edgeLabels) l.destroy();
        this.edgeLabels = [];
        const label = (text: string, cy: number) => {
            const l = new Label({text, fontSize: EDGE_LABEL_FONT, color: lineBlueHeavy, anchorX: 0, anchorY: 0.5});
            l.graphic.position.set(thickness + EDGE_LABEL_GAP, cy);
            this.edge.graphic.addChild(l.graphic);
            this.edgeLabels.push(l);
        };

        const hw = this.elev.hardwareGroup?.hardware ?? [];
        const hinges = this.elev.hardware.hinges;
        const defaultProduct = hinges.getDefaultProduct(hw);
        hinges.list.forEach((h, i) => {
            const hh = hinges.hingeHeightAt(i);
            const cy = h.offsetTop.get();
            g.lineStyle(OUTLINE, lineBlueHeavy, 1);
            g.beginFill(0xffffff, 1);
            g.drawRoundedRect(leafX, cy - hh / 2, leafWidth, hh, CUTOUT_RADIUS);
            g.endFill();

            // a hinge that is not the default reads by name (a power transfer hinge).
            const product = (this.active ? h.activeProductOverride : h.inactiveProductOverride) ?? defaultProduct;
            const item = product !== undefined && product !== defaultProduct ? hw.find(x => x.product === product) : undefined;
            if (item) label(item.productCode, cy);
        });

        // the items in the edge, each named beside it.
        for (const t of this.hardwareThings) {
            t.thing.draw();
            label(t.item.productCode, t.placement.y.get());
        }
    }

    contentBounds(): Rectangle {
        return this.parentThing.graphic.getLocalBounds();
    }

    placeAt(x: number) {
        const b = this.contentBounds();
        this.parentThing.graphic.position.set(x, 0);
        this.caption.graphic.position.set(x + b.x + b.width / 2, b.y - this.captionGap);
    }

    destroy() {
        for (const t of this.hardwareThings) {
            if (!t.thing.destroyed) t.thing.destroy();
        }
        this.hardwareThings = [];
        for (const sub of this.placementSubs) sub.cancel();
        this.placementSubs = [];
        for (const sub of this.hingeSubs) sub.cancel();
        this.hingeSubs = [];
        for (const l of this.edgeLabels) if (!l.destroyed) l.destroy();
        this.edgeLabels = [];
        super.destroy();
    }
}
